import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  ShoppingBag, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Shield,
  Activity,
  FileText,
  FileImage,
  Palette,
  Mail,
  Percent,
  BookOpen,
  Tag,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Beaker,
  Wine,
  Layers,
  Image,
  Tags,
  UserCog,
  Upload,
  Truck,
  ChevronsUp,
  BarChart3,
  LayoutGrid,
  ListTodo
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser, adminLogout } = useAdminAuth();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Carica stato menu da localStorage o usa default
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const saved = localStorage.getItem('adminExpandedMenus');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved menu state:', e);
      }
    }
    // Default: prodotti e ordini aperti
    return {
      products: true,
      orders: true,
      content: false
    };
  });

  const toggleSubmenu = (menu) => {
    setExpandedMenus(prev => {
      const newState = {
        ...prev,
        [menu]: !prev[menu]
      };
      // Salva in localStorage
      localStorage.setItem('adminExpandedMenus', JSON.stringify(newState));
      return newState;
    });
  };

  const collapseAllMenus = () => {
    const collapsed = Object.keys(expandedMenus).reduce((acc, k) => ({ ...acc, [k]: false }), {});
    setExpandedMenus(collapsed);
    localStorage.setItem('adminExpandedMenus', JSON.stringify(collapsed));
  };

  const hasAnyExpanded = Object.values(expandedMenus).some(v => v);

  const handleLogout = async () => {
    try {
      await adminLogout();
      toast({
        title: "Logout effettuato",
        description: "Sei stato disconnesso dal pannello admin",
      });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Errore durante il logout",
        description: "Si è verificato un errore durante la disconnessione",
        variant: "destructive"
      });
    }
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
      accent: 'text-blue-600 bg-blue-50',
      current: location.pathname === '/admin/dashboard'
    },
    {
      name: 'Prodotti',
      icon: Package,
      accent: 'text-amber-600 bg-amber-50',
      submenu: [
        {
          name: 'Prodotti',
          href: '/admin/products',
          icon: Package,
          current: location.pathname === '/admin/products'
        },
        {
          name: 'Ingredienti',
          href: '/admin/ingredients',
          icon: Beaker,
          current: location.pathname === '/admin/ingredients'
        },
        {
          name: 'Bottiglie',
          href: '/admin/bottles',
          icon: Wine,
          current: location.pathname === '/admin/bottles'
        },
        {
          name: 'Formati',
          href: '/admin/formats',
          icon: Layers,
          current: location.pathname === '/admin/formats'
        },
        {
          name: 'Collezioni',
          href: '/admin/collections',
          icon: BookOpen,
          current: location.pathname === '/admin/collections'
        },
        {
          name: 'Sconti Quantità',
          href: '/admin/discount-tiers',
          icon: Percent,
          current: location.pathname === '/admin/discount-tiers'
        }
      ]
    },
    {
      name: 'Ordini & Vendite',
      icon: ShoppingBag,
      accent: 'text-emerald-600 bg-emerald-50',
      submenu: [
        {
          name: 'Ordini',
          href: '/admin/orders',
          icon: ShoppingBag,
          current: location.pathname === '/admin/orders'
        },
        {
          name: 'Preventivi',
          href: '/admin/quotes',
          icon: FileText,
          current: location.pathname === '/admin/quotes'
        },
        {
          name: 'Report',
          href: '/admin/reports',
          icon: BarChart3,
          current: location.pathname === '/admin/reports'
        }
      ]
    },
    {
      name: 'LEAD 2.0',
      icon: LayoutGrid,
      accent: 'text-fuchsia-600 bg-fuchsia-50',
      badge: 'NEW',
      submenu: [
        {
          name: 'Pipeline',
          href: '/admin/leads2',
          icon: LayoutGrid,
          current: location.pathname === '/admin/leads2' || (location.pathname.startsWith('/admin/leads2/') && location.pathname !== '/admin/leads2/todo')
        },
        {
          name: 'TO DO',
          href: '/admin/leads2/todo',
          icon: ListTodo,
          current: location.pathname === '/admin/leads2/todo'
        }
      ]
    },
    {
      name: 'CRM',
      icon: Users,
      accent: 'text-fuchsia-600 bg-fuchsia-50',
      submenu: [
        {
          name: 'Clienti',
          href: '/admin/customers',
          icon: Users,
          current: location.pathname === '/admin/customers' || location.pathname.startsWith('/admin/customers/')
        },
        {
          name: 'Funnel',
          href: '/admin/crm/funnel',
          icon: TrendingUp,
          current: location.pathname === '/admin/crm/funnel'
        },
        {
          name: 'Landing Leads',
          href: '/admin/landing-leads',
          icon: Mail,
          current: location.pathname === '/admin/landing-leads'
        },
        {
          name: 'Import CSV',
          href: '/admin/leads/import',
          icon: Upload,
          current: location.pathname === '/admin/leads/import'
        },
        {
          name: 'Coupons',
          href: '/admin/coupons',
          icon: Tag,
          current: location.pathname === '/admin/coupons'
        },
        {
          name: 'Newsletter',
          href: '/admin/newsletter',
          icon: Mail,
          current: location.pathname === '/admin/newsletter'
        },
        {
          name: 'Templates Email',
          href: '/admin/crm/templates',
          icon: Mail,
          current: location.pathname === '/admin/crm/templates'
        }
      ]
    },
    {
      name: 'Contenuti',
      icon: FileImage,
      accent: 'text-pink-600 bg-pink-50',
      submenu: [
        {
          name: 'Etichette',
          href: '/admin/labels',
          icon: FileImage,
          current: location.pathname === '/admin/labels'
        },
        {
          name: 'Template Canva',
          href: '/admin/template-canva',
          icon: Palette,
          current: location.pathname === '/admin/template-canva'
        },
        {
          name: 'Gallery Liquori',
          href: '/admin/gallery',
          icon: Image,
          current: location.pathname === '/admin/gallery'
        },
        {
          name: 'Recensioni',
          href: '/admin/reviews',
          icon: FileText,
          current: location.pathname === '/admin/reviews'
        },
        {
          name: 'Blog',
          href: '/admin/blog',
          icon: BookOpen,
          current: location.pathname === '/admin/blog'
        }
      ]
    },
    {
      name: 'Utenti',
      href: '/admin/users',
      icon: Users,
      accent: 'text-cyan-600 bg-cyan-50',
      current: location.pathname === '/admin/users'
    },
    {
      name: 'Agenti',
      icon: UserCog,
      accent: 'text-purple-600 bg-purple-50',
      submenu: [
        {
          name: 'Elenco Agenti',
          href: '/admin/agents',
          icon: Users,
          current: location.pathname === '/admin/agents' || (location.pathname.startsWith('/admin/agents/') && !location.pathname.includes('commission'))
        },
        {
          name: 'Piani Provvigionali',
          href: '/admin/agents/commission-plans',
          icon: Percent,
          current: location.pathname === '/admin/agents/commission-plans'
        },
        {
          name: 'Provvigioni',
          href: '/admin/commissions',
          icon: Percent,
          current: location.pathname === '/admin/commissions'
        },
        {
          name: 'Richieste campioni',
          href: '/admin/sample-orders',
          icon: Beaker,
          current: location.pathname === '/admin/sample-orders'
        },
        {
          name: 'Richieste bozze grafiche',
          href: '/admin/graphic-requests',
          icon: Palette,
          current: location.pathname === '/admin/graphic-requests'
        }
      ]
    },
    {
      name: 'Impostazioni',
      icon: Settings,
      accent: 'text-gray-600 bg-gray-100',
      submenu: [
        {
          name: 'Impostazioni Generali',
          href: '/admin/settings',
          icon: Settings,
          current: location.pathname === '/admin/settings'
        },
        {
          name: 'Cat./Ricette Preventivo',
          href: '/admin/lead-categories',
          icon: Layers,
          current: location.pathname === '/admin/lead-categories'
        },
        {
          name: 'Metodi Pagamento',
          href: '/admin/payment-methods',
          icon: Settings,
          current: location.pathname === '/admin/payment-methods'
        },
        {
          name: 'Costi Spedizioni',
          href: '/admin/shipping-costs',
          icon: Truck,
          current: location.pathname === '/admin/shipping-costs'
        },
        {
          name: 'Statistiche',
          href: '/admin/analytics',
          icon: Activity,
          current: location.pathname === '/admin/analytics'
        }
      ]
    }
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-center h-16 px-4 bg-gray-900">
        <Link to="/admin/dashboard" className="flex items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_mixology-admin/artifacts/a22ywdhy_iltuoamaro-white%401000x.webp" 
            alt="Il Tuo Amaro" 
            className="h-8 w-auto"
          />
        </Link>
      </div>

      {/* Toolbar sidebar */}
      <div className="px-4 pt-3">
        <button
          type="button"
          onClick={collapseAllMenus}
          disabled={!hasAnyExpanded}
          data-testid="btn-collapse-all-menus"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-md bg-gray-700/60 text-gray-200 hover:bg-gray-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title={hasAnyExpanded ? 'Chiudi tutti i menu espansi' : 'Nessun menu aperto'}
        >
          <ChevronsUp className="w-4 h-4" />
          <span>Chiudi tutti i menu</span>
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-4 flex-1 overflow-y-auto pb-6">
        <div className="px-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            
            // Item con sottomenu
            if (item.submenu) {
              const menuKey = item.name.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-');
              const hasActiveSub = item.submenu.some(sub => sub.current);
              const isExpanded = (menuKey in expandedMenus) ? expandedMenus[menuKey] : hasActiveSub;
              
              return (
                <div key={item.name}>
                  {/* Menu principale */}
                  <button
                    onClick={() => toggleSubmenu(menuKey)}
                    className={`w-full group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      hasActiveSub
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className={`mr-3 flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 ${item.accent || 'text-gray-400 bg-gray-700/40'} ${hasActiveSub ? 'ring-2 ring-white/40' : ''}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      {item.name}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {/* Sottomenu */}
                  {isExpanded && (
                    <div className="mt-1 ml-4 space-y-1">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              subItem.current
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <SubIcon
                              className={`mr-3 h-4 w-4 flex-shrink-0 ${
                                subItem.current ? 'text-white' : 'text-gray-400 group-hover:text-white'
                              }`}
                            />
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Item normale senza sottomenu
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  item.current
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className={`mr-3 flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 ${item.accent || 'text-gray-400 bg-gray-700/40'} ${item.current ? 'ring-2 ring-white/40' : ''}`}>
                  <Icon className="h-4 w-4" />
                </span>
                {item.name}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Info */}
      <div className="flex-shrink-0 border-t border-gray-700 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {adminUser?.firstName?.[0]}{adminUser?.lastName?.[0]}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-white">
              {adminUser?.firstName} {adminUser?.lastName}
            </p>
            <p className="text-xs text-gray-400">Administrator</p>
          </div>
          <button
            onClick={handleLogout}
            className="ml-3 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex flex-col flex-1 w-64 max-w-xs bg-gray-800">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0 lg:fixed lg:inset-y-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-screen bg-gray-800">
            <SidebarContent />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 lg:pl-64">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 lg:hidden">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="flex items-center">
                <Shield className="h-8 w-8 text-blue-600" />
                <span className="ml-2 text-lg font-semibold text-gray-900">Admin Panel</span>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <LogOut className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-8 px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;